<template>
  <div class="space-y-3">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <h1 class="text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight text-gray-900">
        {{ $t("greeting", { name: currentUser.firstName }) }}
      </h1>
      <SecondaryButton @click="$router.push('/admin/home')">
        <HomeIcon class="h-4 w-4 mr-2" />
        {{ t("dashboard") }}
      </SecondaryButton>
    </div>

    <!-- Data source toggle -->
    <div v-if="hasSeatsOrFurniture" class="flex flex-wrap items-center gap-3">
      <DataSourceToggle v-model="dataSource" />
      <span v-if="(isContractsView || isGrossSalesView) && formattedLastUpdatedAt" class="text-xs text-gray-400 italic">
        {{ t("last_updated_at", { date: formattedLastUpdatedAt }) }}
      </span>
      <button
        v-if="isGrossSalesView && isOrganizationAdmin"
        class="inline-flex items-center gap-1 rounded-md bg-transparent px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-700 transition-colors cursor-pointer"
        @click="showImportModal = true"
      >
        <UploadIcon class="h-3 w-3" />
        {{ t("import_button") }}
      </button>
    </div>
    <GrossSalesImportModal :open="showImportModal" @close="showImportModal = false" />

    <!-- Filters row -->
    <div class="flex flex-wrap items-center gap-2">
      <PeriodComparisonFilter
        v-model:dateRange="dateRange"
        v-model:comparisonType="comparisonType"
        :previousPeriodStartDate="previousPeriodStartDate"
      />
      <div class="w-72">
        <AnalyticsSelector @update-selections="handleSelectionsUpdate" />
      </div>
      <SearchBarFilter
        v-for="(filterOptions, filterName, filterIdx) in availableFilters"
        :key="filterName"
        :filterName="filterName"
        :filterIdx="filterIdx"
        :available-filters="availableFilters"
        :selected-filters="selectedFilters"
        @updateFilterSearch="updateFilterSearch"
      />
    </div>

    <!-- Alert info contextuel -->
    <DataSourceAlert v-if="hasSeatsOrFurniture" :dataSource="dataSource" />

    <!-- Main content -->
    <div v-if="isPresent(generalAnalytics)" class="flex flex-col gap-4">
      <!-- Unified contracts chart + KPI cards block -->
      <SalesChart
        :analytics="generalAnalytics"
        :chart-view="chartView"
        :comparison-type="comparisonType"
        :data-source="dataSource"
      >
        <template #header-right>
          <div v-if="generalData">
            <div class="flex items-center gap-x-2">
              <h1 class="truncate text-sm font-medium text-gray-500">
                {{ t(`today_count_label${dataSourceSuffix}`) }}
              </h1>
              <VTooltip theme="info-tooltip">
                <InformationCircleIcon class="cursor-pointer h-5 w-4 text-gray-400" aria-hidden="true" />
                <template #popper>
                  <span>{{ t(`today_count_tooltip${dataSourceSuffix}`) }}</span>
                </template>
              </VTooltip>
            </div>
            <p class="text-lg pl-0.5 mt-1 tracking-tight text-gray-700">
              {{ generalData.todayContracts?.current }}
              <span v-if="generalData.todayContracts?.previous != null" class="text-sm ml-4 text-gray-500">
                {{ generalData.todayContracts?.previous }} {{ $t("yesterday") }}
              </span>
              <Variation class="ml-2 inline-flex" :variation="generalData.todayContracts?.variation" />
            </p>
          </div>
        </template>
        <template #sidebar>
          <DataCardWithVariation
            v-if="generalData?.storeCommission?.current != null"
            :title="t(`remuneration_title${dataSourceSuffix}`)"
            class="pt-0 mt-0"
            :data="generalData.storeCommission"
            :comparisonType="comparisonType"
          >
            <template #tooltip>
              <span>{{ t(`remuneration_tooltip${dataSourceSuffix}`) }}</span>
              <template v-if="isContractsView">
                <br />
                <br />
                <span>{{ t("remuneration_tooltip_complement") }}</span>
              </template>
            </template>
          </DataCardWithVariation>
          <DataCardWithVariation
            v-if="generalData"
            :title="t('resiliation_title')"
            :tooltipText="t('resiliation_tooltip')"
            :data="generalData.resiliationRate"
            :comparisonType="comparisonType"
          />
        </template>
      </SalesChart>

      <!-- Active stores rate -->
      <ActiveStoresRateCard v-if="generalData?.activeStoresRate" :data="generalData.activeStoresRate" />

      <!-- Sales details (turnover, pie charts, seller ranking) -->
      <SalesAnalytics
        :analytics="generalAnalytics"
        :comparison-type="comparisonType"
        :data-source="dataSource"
        :excluded-store-names="excludedStoreNames"
      >
        <template #stores-ranking-action>
          <router-link to="/admin/analytics/retail" class="text-xs text-gray-400 font-light hover:underline">
            {{ t("see_more") }}
          </router-link>
        </template>
        <template #sellers-ranking-action>
          <router-link to="/admin/analytics/retail" class="text-xs text-gray-400 font-light hover:underline">
            {{ t("see_more") }}
          </router-link>
        </template>
      </SalesAnalytics>

      <!-- Claim cards -->
      <AnalyticsClaimCards :analytics="generalAnalytics" />
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import ActiveStoresRateCard from "@/components/analytics/ActiveStoresRateCard";
import AnalyticsSelector from "@/components/analytics/AnalyticsSelector";
import DataSourceAlert from "@/components/analytics/DataSourceAlert";
import DataSourceToggle from "@/components/analytics/DataSourceToggle";
import GrossSalesImportModal from "@/components/analytics/GrossSalesImportModal";
import PeriodComparisonFilter from "@/components/analytics/PeriodComparisonFilter";
import SalesChart from "@/components/analytics/SalesChart";
import {
  SalesAnalytics,
  AnalyticsClaimCards,
  DataCardWithVariation,
  Variation,
  SearchBarFilter,
  SecondaryButton,
  isPresent,
  currentMonthRange,
  inputDateFormat,
  currentWeek,
  currentYear,
  currentMonth,
} from "@estaly/ui";
import { InformationCircleIcon, UploadIcon, HomeIcon } from "@heroicons/vue/outline";
import { differenceInMonths, format } from "date-fns";
import { fr } from "date-fns/locale";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  components: {
    ActiveStoresRateCard,
    AnalyticsSelector,
    DataSourceAlert,
    DataSourceToggle,
    GrossSalesImportModal,
    PeriodComparisonFilter,
    SalesChart,
    SalesAnalytics,
    AnalyticsClaimCards,
    DataCardWithVariation,
    Variation,
    SearchBarFilter,
    InformationCircleIcon,
    UploadIcon,
    HomeIcon,
    SecondaryButton,
  },

  props: {
    store: {
      type: Object,
      required: false,
    },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      selectedStoreIds: [],
      selectedFilters: {
        ownershipType: [],
        region: [],
      },
      dateRange: currentMonthRange().map((date) => inputDateFormat(date)),
      comparisonType: "previousMonth",
      chartView: "daily",
      dataSource: "contracts",
      showImportModal: false,
    };
  },

  computed: {
    ...mapState("auth", ["currentUser"]),
    ...mapState("analytics", ["generalAnalytics"]),
    ...mapGetters("analytics", ["generalData", "previousPeriodStartDate", "lastUpdatedAt"]),
    ...mapGetters("businessVerticals", ["hasExternalPosOnlySubscription", "hasSeatsOrFurniture"]),
    ...mapGetters("auth", ["isOrganizationAdmin", "hasRetailAnalytics"]),
    ...mapGetters("store", ["getOwnershipTypes"]),
    ...mapGetters("organization", ["regions"]),

    isLeadsView() {
      return this.dataSource === "leads";
    },

    isGrossSalesView() {
      return this.dataSource === "gross_sales";
    },

    isContractsView() {
      return this.dataSource === "contracts";
    },

    dataSourceSuffix() {
      if (this.isContractsView) return "";
      if (this.isLeadsView) return "_leads";
      return "_gross_sales";
    },

    formattedLastUpdatedAt() {
      if (!this.lastUpdatedAt) return null;
      return format(new Date(this.lastUpdatedAt), "dd/MM/yyyy 'à' HH'h'mm", { locale: fr });
    },

    ownershipTypeFilterOptions() {
      if (this.store || !this.hasRetailAnalytics) return [];
      return this.getOwnershipTypes().map((value) => ({
        value,
        label: this.$t(`store.ownership_type.${value}`),
      }));
    },

    regionFilterOptions() {
      if (this.store || !this.hasExternalPosOnlySubscription || !this.isOrganizationAdmin) return [];
      return this.regions.map((region) => ({ value: region, label: region }));
    },

    availableFilters() {
      const filters = {};
      if (this.ownershipTypeFilterOptions.length) {
        filters.ownershipType = this.ownershipTypeFilterOptions;
      }
      if (this.regionFilterOptions.length) {
        filters.region = this.regionFilterOptions;
      }
      return filters;
    },

    excludedStoreNames() {
      return this.isContractsView ? [] : ["BUT ESTALY CONSEIL"];
    },

    apiParams() {
      return {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        comparisonType: this.comparisonType,
        dataSource: this.dataSource,
        ownershipType: this.selectedFilters.ownershipType,
        region: this.selectedFilters.region,
      };
    },
  },

  watch: {
    comparisonType() {
      this.requestAnalytics();
    },
    dataSource() {
      this.requestAnalytics();
    },
    dateRange(newValue) {
      this.autoSwitchChartView(newValue);
      const defaultComparison = this.getDefaultComparisonTypeForRange(newValue);
      if (defaultComparison !== this.comparisonType) {
        this.comparisonType = defaultComparison;
      } else {
        this.requestAnalytics();
      }
    },

    hasSeatsOrFurniture(newValue) {
      this.dataSource = newValue ? "gross_sales" : "contracts";
    },
  },

  created() {
    this.fetchOrganization();
    if (this.hasSeatsOrFurniture) {
      this.dataSource = "gross_sales"; // watcher triggers requestAnalytics
    } else {
      this.requestAnalytics();
    }
  },

  methods: {
    ...mapActions("store", ["withLoader"]),
    ...mapActions("organization", { fetchOrganization: "fetch" }),
    ...mapActions("analytics", ["fetchGeneralAnalytics"]),
    isPresent,

    getDefaultComparisonTypeForRange(dateRange) {
      const [startDate, endDate] = dateRange.map((d) => new Date(d));

      if (currentWeek(startDate, endDate)) return "previousWeek";
      if (currentYear(startDate, endDate)) return "previousYear";
      if (currentMonth(startDate, endDate)) return "previousMonth";

      return "previousPeriod";
    },

    autoSwitchChartView(dateRange) {
      const [startDate, endDate] = dateRange.map((d) => new Date(d));
      const monthsDifference = differenceInMonths(endDate, startDate);

      if (monthsDifference > 2) {
        this.chartView = "monthly";
      } else {
        this.chartView = "daily";
      }
    },

    requestAnalytics() {
      const params = { stores: this.selectedStoreIds, ...this.apiParams };
      if (this.store) params.stores = [this.store.id];
      this.withLoader(() => this.fetchGeneralAnalytics(params));
    },

    updateFilterSearch(filterName, value) {
      this.selectedFilters[filterName] = value;
      this.requestAnalytics();
    },

    handleSelectionsUpdate(selections) {
      if (selections.stores.length > 0) {
        this.selectedStoreIds = selections.stores.map((store) => store.id);
      } else {
        this.selectedStoreIds = [];
      }

      this.requestAnalytics();
    },
  },
};
</script>

<i18n src="./GeneralAnalytics.i18n.json"></i18n>
